// weightEstimate.js
// F-WEIGHT-ESTIMATE Phase 2 — shared storefront helper for the catch-weight
// price ESTIMATE shown on PACKAGE items.
//
// Single source of truth for the estimate math and display strings so the PDP,
// Cart, and Checkout can't drift. Display-only: nothing here is charged. The
// actual amount is computed server-side at promote/weighing.
//
// THE MODEL (package-only, per migration 152):
//   - 'package' = pre-packaged catch-weight. The real weight (and therefore the
//     price) isn't known until the producer weighs the package at fulfillment,
//     AFTER the customer orders. So we show an ESTIMATE = avg_weight x rate,
//     where `rate` is the catalog price (a per-UOM rate, e.g. $10/lb) and
//     avg_weight is the typical package weight in that same UOM. Required field.
//   - 'bulk'    = deterministic qty x rate sale (in-person register scale, or —
//     once F-BULK-ONLINE ships — an online customer-entered exact weight). No
//     estimate, no avg_weight. For now the storefront still shows bulk as
//     "priced by weight / at weighing" with no number.
//   - 'each'    = fixed price per unit. Not weight-priced at all.
//
// Because only 'package' carries avg_weight, the estimate helpers below are
// gated on sold_by === 'package' AND a positive avg_weight AND a UOM. When any
// of those is missing we fall back to the interim "Price at weighing"
// presentation (hasEstimate === false), so a package item that somehow lacks an
// avg_weight degrades gracefully rather than showing a broken "$NaN".

const UOM_LABEL = { lb: 'lb', oz: 'oz', kg: 'kg', g: 'g' };

// Human-friendly unit label for a weight UOM code, generic fallback.
export function unitLabel(uom) {
  return UOM_LABEL[uom] || 'unit';
}

// True when this item/line is sold by weight (package or bulk). Mirrors the
// server split and the existing isPackagedItem()/isPricedByWeight() predicates.
export function isWeightPriced(itemLike) {
  const soldBy = itemLike && itemLike.sold_by;
  return soldBy === 'package' || soldBy === 'bulk';
}

// True only for the mode that carries an estimate: 'package'.
export function isPackageMode(itemLike) {
  return !!itemLike && itemLike.sold_by === 'package';
}

// True for deterministic charge-now bulk: customer enters exact weight online.
export function isBulkMode(itemLike) {
  return !!itemLike && itemLike.sold_by === 'bulk';
}

// Bulk weight input bounds — mirror sitePublicCheckout.js MAX_BULK_LINE_WEIGHT.
export const BULK_WEIGHT_MIN = 0.01;
export const BULK_WEIGHT_STEP = 0.01;
export const MAX_BULK_LINE_WEIGHT = 99;

/**
 * Exact line total for a bulk item: weight × per-UOM rate (2dp).
 * Returns null when rate or weight is not a positive finite number.
 */
export function computeBulkLineTotal(itemLike, weight) {
  const rate = positiveNumber(itemLike && itemLike.price);
  const w = positiveNumber(weight);
  if (rate === null || w === null) return null;
  return Math.round(rate * w * 100) / 100;
}

// Coerce a value to a finite positive number, or null.
function positiveNumber(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Compute the per-unit (single package) estimate for a catch-weight package.
 *
 * @param {object} itemLike  a product or cart line: needs sold_by, price (the
 *                           per-UOM rate), avg_weight, unit_of_measure.
 * @returns {{
 *   hasEstimate: boolean,   // true only when package + positive rate + avg_weight + UOM
 *   rate: number|null,      // per-UOM rate (catalog price)
 *   avgWeight: number|null, // typical package weight, in `uom`
 *   uom: string,            // unit label ('lb' etc.) or 'unit'
 *   unitEstimate: number|null, // estimated price for ONE package (rate * avgWeight)
 * }}
 *
 * When hasEstimate is false the caller should fall back to the interim
 * "Price at weighing" presentation (no number).
 */
export function computeUnitEstimate(itemLike) {
  const rate = positiveNumber(itemLike && itemLike.price);
  const avgWeight = positiveNumber(itemLike && itemLike.avg_weight);
  const uomCode = itemLike && itemLike.unit_of_measure;
  const uom = unitLabel(uomCode);

  // Estimate is meaningful only for 'package' with a positive rate, a positive
  // avg_weight, and a real UOM. Bulk/each (or a package missing data) -> none.
  const hasEstimate =
    isPackageMode(itemLike) && rate !== null && avgWeight !== null && !!UOM_LABEL[uomCode];

  return {
    hasEstimate,
    rate,
    avgWeight,
    uom,
    unitEstimate: hasEstimate ? rate * avgWeight : null,
  };
}

/**
 * Line-level estimate: the per-package estimate times the quantity (number of
 * packages). Quantity defaults to 1 so this also serves the PDP (single unit).
 *
 * @returns same shape as computeUnitEstimate plus:
 *   quantity: number,
 *   lineEstimate: number|null   // unitEstimate * quantity
 */
export function computeLineEstimate(itemLike, quantity = 1) {
  const base = computeUnitEstimate(itemLike);
  const qty = Number.isFinite(Number(quantity)) && Number(quantity) > 0 ? Number(quantity) : 1;
  return {
    ...base,
    quantity: qty,
    lineEstimate: base.hasEstimate ? base.unitEstimate * qty : null,
  };
}

// Money formatter (USD, 2dp). Kept local so callers don't each re-implement it.
export function formatMoney(n) {
  const v = Number(n);
  return Number.isFinite(v) ? `$${v.toFixed(2)}` : '';
}

// Trim trailing zeros from an avg-weight for display: 1.7000 -> "1.7",
// 2.0000 -> "2", 1.2500 -> "1.25". Keeps up to 4 decimals (the column's scale).
function trimWeight(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '';
  return String(parseFloat(v.toFixed(4)));
}

/**
 * Short estimate label for ONE package, e.g.
 *   "est. $17.00  ·  avg 1.7 lb @ $10.00/lb  ·  final price at weighing"
 * Returns null when there is no estimate (caller falls back to "Price at
 * weighing"). Trims trailing zeros on the avg-weight so "1.70" reads "1.7".
 */
export function unitEstimateLabel(itemLike) {
  const e = computeUnitEstimate(itemLike);
  if (!e.hasEstimate) return null;
  const avg = trimWeight(e.avgWeight);
  return `est. ${formatMoney(e.unitEstimate)} · avg ${avg} ${e.uom} @ ${formatMoney(e.rate)}/${e.uom} · final price at weighing`;
}

/**
 * Line estimate label (quantity-aware). For a single package it reads like the
 * unit label; for multiple it makes the per-package basis explicit, e.g.
 *   "est. $34.00  ·  2 × avg 1.7 lb @ $10.00/lb  ·  final price at weighing"
 * Returns null when there is no estimate.
 */
export function lineEstimateLabel(itemLike, quantity = 1) {
  const e = computeLineEstimate(itemLike, quantity);
  if (!e.hasEstimate) return null;
  const avg = trimWeight(e.avgWeight);
  if (e.quantity === 1) {
    return `est. ${formatMoney(e.lineEstimate)} · avg ${avg} ${e.uom} @ ${formatMoney(e.rate)}/${e.uom} · final price at weighing`;
  }
  return `est. ${formatMoney(e.lineEstimate)} · ${e.quantity} × avg ${avg} ${e.uom} @ ${formatMoney(e.rate)}/${e.uom} · final price at weighing`;
}
